import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart2, Calculator, FileText, Target, Zap, CheckCircle, 
  TrendingUp, Users, BookOpen, MessageSquare, Settings,
  X, ExternalLink, Calendar, Star, Brain, ArrowRight,
  Clock, Trophy, AlertCircle
} from 'lucide-react';
import { useAdaptiveDashboard, DashboardLayout } from '@/contexts/AdaptiveDashboardContext';
import { cn } from '@/lib/utils';

interface DashboardLayoutProps {
  layout: DashboardLayout;
  userCompletions: any[];
  recommendations: any[];
  onLayoutChange: (layout: DashboardLayout) => void;
}

const AdaptiveDashboardLayout = ({ layout, userCompletions, recommendations, onLayoutChange }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEntry, dismissRecommendation, completeRecommendation } = useAdaptiveDashboard();

  // Track dashboard entry
  useEffect(() => {
    const entryPath = location.state?.from || 'direct';
    trackEntry(entryPath);
  }, []);

  const tools = [
    { name: 'loaded_score', label: 'Loaded Score', icon: BarChart2, path: '/score', color: 'text-accent-blue' },
    { name: 'rate_whisperer', label: 'Rate Whisperer', icon: Calculator, path: '/rate-whisperer', color: 'text-success' },
    { name: 'career_artifacts', label: 'Career Artifacts', icon: FileText, path: '/artifacts', color: 'text-primary' },
    { name: 'opportunity_audit', label: 'Opportunity Audit', icon: Target, path: '/opportunity-audit', color: 'text-warning' },
    { name: 'power_moves', label: 'Power Moves', icon: Zap, path: '/power-moves', color: 'text-accent-pink' },
    { name: 'okrs', label: 'OKRs', icon: CheckCircle, path: '/okrs', color: 'text-secondary' },
    { name: 'big_sheet', label: 'Big Sheet', icon: Calendar, path: '/big-sheet', color: 'text-accent-cyan' },
  ];

  const getCompletionData = (toolName: string) => {
    return userCompletions.find(c => c.tool_name === toolName) || {
      completion_status: 'not_started',
      completion_percentage: 0
    };
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-success';
    if (percentage >= 50) return 'bg-accent-yellow';
    return 'bg-red-500';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'completed': { label: 'Complete', variant: 'default' as const, color: 'bg-success' },
      'in_progress': { label: 'In Progress', variant: 'secondary' as const, color: 'bg-accent-yellow' },
      'not_started': { label: 'Not Started', variant: 'outline' as const, color: 'bg-border' },
      'needs_update': { label: 'Needs Update', variant: 'destructive' as const, color: 'bg-warning' },
    };
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started;
  };

  const handleRecommendationClick = async (rec: any) => {
    await completeRecommendation(rec.id);
    if (rec.action_url) {
      navigate(rec.action_url);
    }
  };

  const renderLayoutSpecificContent = () => {
    switch (layout) {
      case 'score-focused':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Assessment Progress - Primary */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <BarChart2 className="h-5 w-5" />
                    Assessment Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {tools.slice(0, 3).map(tool => {
                      const completion = getCompletionData(tool.name);
                      const statusBadge = getStatusBadge(completion.completion_status);
                      return (
                        <div key={tool.name} className="flex items-center justify-between p-4 bg-background rounded-lg border">
                          <div className="flex items-center gap-3">
                            <tool.icon className={cn("h-6 w-6", tool.color)} />
                            <div>
                              <div className="font-semibold">{tool.label}</div>
                              <Badge variant={statusBadge.variant} className="text-xs">
                                {statusBadge.label}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-24">
                              <Progress 
                                value={completion.completion_percentage} 
                                className={getProgressColor(completion.completion_percentage)}
                              />
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => navigate(tool.path)}
                              variant={completion.completion_status === 'completed' ? 'outline' : 'default'}
                            >
                              {completion.completion_status === 'completed' ? 'Review' : 'Continue'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions - Secondary */}
            <div className="space-y-6">
              {renderRecommendations()}
              {renderQuickActions()}
            </div>
          </div>
        );

      case 'rate-focused':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Rate Tools - Primary */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-green-500/20 bg-gradient-to-br from-green-500/5 to-emerald-500/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-success">
                    <Calculator className="h-5 w-5" />
                    Rate & Financial Tools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[tools[1], { name: 'crystal_ball', label: 'Rate Crystal Ball', icon: Star, path: '/rate-whisperer?mode=crystal-ball', color: 'text-accent-yellow' }].map(tool => {
                      const completion = getCompletionData(tool.name);
                      return (
                        <div key={tool.name} className="p-6 bg-background rounded-lg border hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate(tool.path)}>
                          <div className="flex items-center gap-3 mb-4">
                            <tool.icon className={cn("h-8 w-8", tool.color)} />
                            <div>
                              <div className="font-semibold text-lg">{tool.label}</div>
                              <div className="text-sm text-muted-foreground">
                                {completion.completion_percentage}% complete
                              </div>
                            </div>
                          </div>
                          <Progress value={completion.completion_percentage} className="mb-4" />
                          <Button className="w-full" variant={completion.completion_status === 'completed' ? 'outline' : 'default'}>
                            {completion.completion_status === 'completed' ? 'Review Results' : 'Get Started'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Rate Intelligence - Secondary */}
            <div className="space-y-6">
              {renderRecommendations()}
              {renderProgressOverview()}
            </div>
          </div>
        );

      case 'journey-focused':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Career Journey Tools - Primary */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-pink-500/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <FileText className="h-5 w-5" />
                    Career Journey & Documentation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[tools[2], tools[6]].map(tool => {
                      const completion = getCompletionData(tool.name);
                      return (
                        <div key={tool.name} className="p-6 bg-background rounded-lg border">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <tool.icon className={cn("h-6 w-6", tool.color)} />
                              <div>
                                <div className="font-semibold">{tool.label}</div>
                                <div className="text-sm text-muted-foreground">
                                  Build your career story
                                </div>
                              </div>
                            </div>
                            <Badge variant={getStatusBadge(completion.completion_status).variant}>
                              {getStatusBadge(completion.completion_status).label}
                            </Badge>
                          </div>
                          <Progress value={completion.completion_percentage} className="mb-4" />
                          <Button onClick={() => navigate(tool.path)} className="w-full">
                            {completion.completion_status === 'completed' ? 'Review & Update' : 'Continue Building'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Journey Insights - Secondary */}
            <div className="space-y-6">
              {renderRecommendations()}
              {renderAchievements()}
            </div>
          </div>
        );

      case 'decision-focused':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Decision Tools - Primary */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-red-500/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-warning">
                    <Target className="h-5 w-5" />
                    Decision & Planning Tools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[tools[3], tools[4], tools[5]].map(tool => {
                      const completion = getCompletionData(tool.name);
                      return (
                        <div key={tool.name} className="p-4 bg-background rounded-lg border hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(tool.path)}>
                          <div className="flex items-center gap-3 mb-3">
                            <tool.icon className={cn("h-6 w-6", tool.color)} />
                            <div className="font-semibold">{tool.label}</div>
                          </div>
                          <Progress value={completion.completion_percentage} className="mb-3" />
                          <div className="text-sm text-muted-foreground mb-3">
                            {completion.completion_percentage}% complete
                          </div>
                          <Button size="sm" variant={completion.completion_status === 'completed' ? 'outline' : 'default'}>
                            {completion.completion_status === 'completed' ? 'Review' : 'Start'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Decision Insights - Secondary */}
            <div className="space-y-6">
              {renderRecommendations()}
              {renderDecisionMetrics()}
            </div>
          </div>
        );

      default: // balanced
        return (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {renderProgressOverview()}
              {renderToolsGrid()}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {renderRecommendations()}
              {renderQuickActions()}
            </div>
          </div>
        );
    }
  };

  const renderRecommendations = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Brain className="h-4 w-4 text-purple-500" />
          Smart Recommendations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {recommendations.slice(0, 3).map(rec => (
          <div key={rec.id} className="p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-medium text-sm">{rec.title}</div>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-6 w-6 shrink-0"
                onClick={() => dismissRecommendation(rec.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{rec.description}</p>
            <Button 
              size="sm" 
              className="w-full text-xs"
              onClick={() => handleRecommendationClick(rec)}
            >
              Take Action
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        ))}
        {recommendations.length === 0 && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            <Trophy className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
            You're all caught up!
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderQuickActions = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start"
          onClick={() => navigate('/workspace')}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Chat with AI
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start"
          onClick={() => navigate('/artifacts')}
        >
          <FileText className="h-4 w-4 mr-2" />
          Add Achievement
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start"
          onClick={() => navigate('/score')}
        >
          <BarChart2 className="h-4 w-4 mr-2" />
          Update Score
        </Button>
      </CardContent>
    </Card>
  );

  const renderProgressOverview = () => {
    const totalTools = tools.length;
    const completedTools = userCompletions.filter(c => c.completion_status === 'completed').length;
    const overallProgress = totalTools > 0 ? (completedTools / totalTools) * 100 : 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Overall Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-4">
            <div className="text-3xl font-bold text-primary">{Math.round(overallProgress)}%</div>
            <div className="text-sm text-muted-foreground">Career Foundation Complete</div>
          </div>
          <Progress value={overallProgress} className="mb-4" />
          <div className="text-sm text-muted-foreground">
            {completedTools} of {totalTools} tools completed
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderToolsGrid = () => (
    <Card>
      <CardHeader>
        <CardTitle>Career Tools</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map(tool => {
            const completion = getCompletionData(tool.name);
            const statusBadge = getStatusBadge(completion.completion_status);
            return (
              <div 
                key={tool.name} 
                className="p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(tool.path)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <tool.icon className={cn("h-6 w-6", tool.color)} />
                  <div className="font-semibold">{tool.label}</div>
                </div>
                <Progress value={completion.completion_percentage} className="mb-3" />
                <div className="flex items-center justify-between">
                  <Badge variant={statusBadge.variant} className="text-xs">
                    {statusBadge.label}
                  </Badge>
                  <div className="text-sm text-muted-foreground">
                    {completion.completion_percentage}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderAchievements = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-500" />
          Recent Achievements
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-3 w-3" />
            No recent achievements
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderDecisionMetrics = () => (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-orange-500" />
          Decision Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Opportunities Evaluated</span>
            <span className="font-medium">0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Decisions Made</span>
            <span className="font-medium">0</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Layout Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Dashboard Layout
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'balanced', label: 'Balanced' },
              { key: 'score-focused', label: 'Assessment' },
              { key: 'rate-focused', label: 'Rate Tools' },
              { key: 'journey-focused', label: 'Journey' },
              { key: 'decision-focused', label: 'Decisions' },
            ].map(layoutOption => (
              <Button
                key={layoutOption.key}
                size="sm"
                variant={layout === layoutOption.key ? 'default' : 'outline'}
                onClick={() => onLayoutChange(layoutOption.key as DashboardLayout)}
                className="text-xs"
              >
                {layoutOption.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Layout Content */}
      {renderLayoutSpecificContent()}
    </div>
  );
};

export default AdaptiveDashboardLayout;